import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Storefront, ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { landingPath } from "@/lib/perms";
import { fileUrl } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LOGO = "/logo-poss.png";
const CODE_KEY = "pos_codigo";

export default function Login() {
  const { user, login, companyLookup, posLogin, error } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("pos"); // pos | admin
  const [step, setStep] = useState("company"); // company | credentials
  const [codigo, setCodigo] = useState("");
  const [company, setCompany] = useState(null);
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const confirmCompany = useCallback(async (code) => {
    const data = await companyLookup(code);
    setCompany(data);
    setCodigo(data.codigo);
    setStep("credentials");
    localStorage.setItem(CODE_KEY, data.codigo);
    return data;
  }, [companyLookup]);

  // El dispositivo recuerda el código de empresa permanentemente.
  useEffect(() => {
    const saved = localStorage.getItem(CODE_KEY);
    if (saved && mode === "pos" && step === "company") {
      confirmCompany(saved).catch(() => localStorage.removeItem(CODE_KEY));
    }
  }, [mode, step, confirmCompany]);

  useEffect(() => {
    if (user) navigate(landingPath(user), { replace: true });
  }, [user, navigate]);

  const submitCompany = async (e) => {
    e.preventDefault();
    if (!codigo.trim()) return;
    setLoading(true);
    try { await confirmCompany(codigo.trim()); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };

  const submitPos = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) return toast.error("El PIN debe tener 4 dígitos");
    setLoading(true);
    const ok = await posLogin(company.codigo, username.trim(), pin);
    setLoading(false);
    if (!ok) return;
  };

  const submitAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email.trim(), password);
    setLoading(false);
    if (!ok) return;
  };

  const changeCompany = () => {
    localStorage.removeItem(CODE_KEY);
    setCompany(null); setCodigo(""); setUsername(""); setPin(""); setStep("company");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative brand-dark items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #d4af37 0%, transparent 40%), radial-gradient(circle at 80% 80%, #d4af37 0%, transparent 40%)" }} />
        <div className="relative text-center px-10">
          <img src={LOGO} alt="Inmobiliaria POSS" className="w-72 h-72 mx-auto rounded-2xl object-cover shadow-2xl shadow-amber-500/10 ring-1 ring-amber-400/20" />
          <p className="mt-6 text-amber-100/70 text-sm max-w-sm mx-auto">
            Acceso rápido por código de empresa y PIN. Sin correos, sin contraseñas complicadas.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO} alt="POSS" className="w-11 h-11 rounded-lg object-cover ring-1 ring-amber-400/30" />
            <div>
              <div className="font-display font-extrabold text-lg">Inmobiliaria <span className="brand-gold-text">POSS</span></div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Sistema de Administración</div>
            </div>
          </div>

          {mode === "admin" ? (
            <form onSubmit={submitAdmin} data-testid="admin-login-form">
              <h1 className="text-2xl font-display font-bold mb-1">Acceso administrador</h1>
              <p className="text-sm text-muted-foreground mb-6">Panel Superadmin de licencias.</p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Correo</Label>
                  <Input id="email" data-testid="login-email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} className="rounded-sm mt-1" required />
                </div>
                <div>
                  <Label htmlFor="password">Contraseña</Label>
                  <Input id="password" data-testid="login-password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} className="rounded-sm mt-1" required />
                </div>
                {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
                <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full rounded-sm">
                  {loading ? "Ingresando…" : "Entrar"}
                </Button>
                <button type="button" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  data-testid="back-to-pos" onClick={() => setMode("pos")}>
                  <ArrowLeft size={12} /> Volver al acceso de empresa
                </button>
              </div>
            </form>
          ) : step === "company" ? (
            <form onSubmit={submitCompany} data-testid="company-form">
              <h1 className="text-2xl font-display font-bold mb-1">Código de empresa</h1>
              <p className="text-sm text-muted-foreground mb-6">Ingresa el código de tu empresa para continuar.</p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="codigo">Código</Label>
                  <Input id="codigo" data-testid="codigo-input" value={codigo}
                    onChange={(e) => setCodigo(e.target.value.toUpperCase())} placeholder="Ej. POS001"
                    className="rounded-sm mt-1 uppercase tracking-widest text-lg font-mono" required autoFocus />
                </div>
                <Button data-testid="codigo-submit" type="submit" disabled={loading} className="w-full rounded-sm">
                  {loading ? "Buscando…" : "Continuar"}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={submitPos} data-testid="pos-form">
              <div className="flex items-center gap-3 mb-6 p-3 rounded-sm border border-border bg-card" data-testid="company-confirm">
                {company?.logo
                  ? <img src={fileUrl(company.logo)} alt={company.empresa} className="w-12 h-12 rounded-sm object-cover" />
                  : <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center"><Storefront size={24} className="text-primary" /></div>}
                <div className="min-w-0">
                  <div className="font-display font-bold truncate" data-testid="company-name">{company?.empresa}</div>
                  <div className="text-xs text-muted-foreground font-mono">{company?.codigo}</div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="username">Usuario</Label>
                  <Input id="username" data-testid="username-input" value={username}
                    onChange={(e) => setUsername(e.target.value)} className="rounded-sm mt-1" required autoFocus />
                </div>
                <div>
                  <Label htmlFor="pin">PIN (4 dígitos)</Label>
                  <Input id="pin" data-testid="pin-input" type="password" inputMode="numeric" maxLength={4} value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    className="rounded-sm mt-1 tracking-[0.5em] text-lg font-mono" required />
                </div>
                {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
                <Button data-testid="pos-submit" type="submit" disabled={loading} className="w-full rounded-sm">
                  {loading ? "Ingresando…" : "Entrar"}
                </Button>
                <button type="button" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  data-testid="change-company" onClick={changeCompany}>
                  <ArrowLeft size={12} /> Cambiar empresa
                </button>
              </div>
            </form>
          )}

          {mode === "pos" && (
            <button type="button" data-testid="admin-access-link"
              className="mt-8 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              onClick={() => setMode("admin")}>
              <ShieldCheck size={12} /> Acceso administrador (Superadmin)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
